import React from 'react';
import { GdashReportResponse, inboundInboxService, ShiftGuardianSettings } from '../../services/inboundInboxService';
import { GDashCheckType, GDashPopup } from '../GDash/GDashPopup';

const rowClass = 'grid grid-cols-1 gap-2 md:grid-cols-[260px,1fr] md:items-center';
const labelClass = 'text-xs font-semibold uppercase tracking-[0.14em] text-slate-500';
const inputClass = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700';
const STATUS_EVENT = 'gdash:scheduler-status';
const TRIGGER_EVENT = 'gdash:trigger-now';
const CLEAR_DISMISSALS_EVENT = 'gdash:clear-dismissals';
const MOUNT_EVENT = 'gdash:scheduler-mounted';
const metricToneClass = (value: number): string => {
  if (value > 10) return 'text-rose-700';
  if (value > 5) return 'text-amber-700';
  return 'text-slate-900';
};

const defaults: ShiftGuardianSettings = {
  enabled: false,
  showToOperators: true,
  showToAdmins: true,
  timezone: 'Africa/Dar_es_Salaam',
  morningBriefEnabled: true,
  morningBriefTime: '08:00',
  middayCheckEnabled: true,
  middayCheckTime: '13:00',
  endOfDayCheckEnabled: true,
  endOfDayCheckTime: '17:30',
  shiftEndTime: '18:00',
  refreshSeconds: 60,
  guardianLookbackHours: 24,
  mediumRiskAgeMinutes: 120,
  highRiskAgeMinutes: 240,
  includeUnassigned: true,
  operatorsCanSeeAssignedToOthers: false,
  gdashActionsEnabled: true,
  allowAdminOverride: true,
  operatorCanAssignToSelf: true,
  operatorCanSetFollowup: true,
  operatorCanMarkSafe: false,
  maxItems: 50,
  aiSuggestionsEnabled: false,
};

export const GDashTab: React.FC = () => {
  const [settings, setSettings] = React.useState<ShiftGuardianSettings>(defaults);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [status, setStatus] = React.useState<string | null>(null);
  const [testing, setTesting] = React.useState(false);
  const [testStatus, setTestStatus] = React.useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewType, setPreviewType] = React.useState<GDashCheckType>('morning');
  const [previewSummary, setPreviewSummary] = React.useState<any | null>(null);
  const [schedulerDiag, setSchedulerDiag] = React.useState<{
    timezone: string;
    currentDate: string;
    currentTime: string;
    nextCheckLabel: string;
    lastCheckAt: string;
    lastDecision: string;
    reason: string;
  } | null>(null);
  const [schedulerRawPayload, setSchedulerRawPayload] = React.useState<Record<string, any> | null>(null);
  const [schedulerMounted, setSchedulerMounted] = React.useState(false);
  const [schedulerLastHeartbeatAt, setSchedulerLastHeartbeatAt] = React.useState<string>('n/a');
  const [reportDate, setReportDate] = React.useState<string>(() => new Date().toISOString().slice(0, 10));
  const [reportTeam, setReportTeam] = React.useState<'all' | 'reservations' | 'flight_planners'>('all');
  const [reportOperatorUserId, setReportOperatorUserId] = React.useState<string>('');
  const [reportIncludeDetails, setReportIncludeDetails] = React.useState<boolean>(true);
  const [reportLoading, setReportLoading] = React.useState<boolean>(false);
  const [reportError, setReportError] = React.useState<string | null>(null);
  const [reportData, setReportData] = React.useState<GdashReportResponse | null>(null);
  const [expandedOperators, setExpandedOperators] = React.useState<Record<string, boolean>>({});
  const pendingLabel = reportData?.pendingLabelMode === 'NOW'
    ? 'Still unresolved after interaction (now)'
    : 'Still unresolved after interaction';

  const load = React.useCallback(async () => {
    setLoading(true);
    setStatus(null);
    try {
      const next = await inboundInboxService.getGdashSettings();
      setSettings(next);
    } catch (error: any) {
      setStatus(error?.response?.data?.error || error?.message || 'Could not load G.DASH settings');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);

  const loadReport = React.useCallback(async () => {
    setReportLoading(true);
    setReportError(null);
    try {
      const data = await inboundInboxService.getGdashReport({
        date: reportDate,
        timezone: settings.timezone,
        team: reportTeam,
        operatorUserId: reportOperatorUserId || undefined,
        includeDetails: reportIncludeDetails,
      });
      setReportData(data);
    } catch (error: any) {
      setReportError(error?.response?.data?.error || error?.message || 'Could not load report');
    } finally {
      setReportLoading(false);
    }
  }, [reportDate, reportIncludeDetails, reportOperatorUserId, reportTeam, settings.timezone]);

  React.useEffect(() => {
    void loadReport();
  }, [loadReport]);

  const save = React.useCallback(async () => {
    setSaving(true);
    setStatus(null);
    try {
      const next = await inboundInboxService.updateGdashSettings({ ...settings, aiSuggestionsEnabled: false });
      setSettings(next);
      setStatus('Saved');
    } catch (error: any) {
      setStatus(error?.response?.data?.error || error?.message || 'Could not save G.DASH settings');
    } finally {
      setSaving(false);
    }
  }, [settings]);

  const testGuardian = React.useCallback(async () => {
    setTesting(true);
    setTestStatus(null);
    try {
      const res = await inboundInboxService.getShiftGuardianSummary();
      setTestStatus(`enabled=${res.enabled} reason=${res.reason || 'none'} items=${Array.isArray(res.items) ? res.items.length : 0}`);
    } catch (error: any) {
      setTestStatus(error?.response?.data?.error || error?.message || 'Test failed');
    } finally {
      setTesting(false);
    }
  }, []);

  const openPreview = React.useCallback(async (type: GDashCheckType) => {
    setPreviewType(type);
    const res = await inboundInboxService.getShiftGuardianSummary({ checkType: type });
    setPreviewSummary(res);
    setPreviewOpen(true);
    setTestStatus(`enabled=${res.enabled} reason=${res.reason || 'none'} items=${Array.isArray(res.items) ? res.items.length : 0} generatedAt=${res.generatedAt || 'n/a'}`);
  }, []);

  const triggerNow = React.useCallback((type: GDashCheckType) => {
    if (!schedulerMounted) {
      setTestStatus('Scheduler is not active on this page.');
      return;
    }
    window.dispatchEvent(new CustomEvent(TRIGGER_EVENT, { detail: { checkType: type } }));
  }, [schedulerMounted]);

  const clearTodayDismissals = React.useCallback(() => {
    window.dispatchEvent(new CustomEvent(CLEAR_DISMISSALS_EVENT));
    setTestStatus('Requested dismissal reset for today (current user).');
  }, []);

  React.useEffect(() => {
    const onStatus = (event: Event) => {
      const detail = (event as CustomEvent<any>).detail;
      if (!detail || typeof detail !== 'object') return;
      setSchedulerRawPayload(detail);
      setSchedulerDiag({
        timezone: String(detail.timezone || 'Africa/Dar_es_Salaam'),
        currentDate: String(detail.currentDate || ''),
        currentTime: String(detail.currentTime || ''),
        nextCheckLabel: String(detail.nextCheckLabel || 'n/a'),
        lastCheckAt: String(detail.lastCheckAt || ''),
        lastDecision: String(detail.lastDecision || 'n/a'),
        reason: String(detail.reason || 'n/a'),
      });
    };
    window.addEventListener(STATUS_EVENT, onStatus as EventListener);
    return () => {
      window.removeEventListener(STATUS_EVENT, onStatus as EventListener);
    };
  }, []);

  React.useEffect(() => {
    if (document.body.dataset.gdashMounted === 'true') {
      setSchedulerMounted(true);
      setSchedulerLastHeartbeatAt(new Date().toISOString());
    }
    const onMounted = (event: Event) => {
      const detail = (event as CustomEvent<any>).detail || {};
      setSchedulerMounted(Boolean(detail.mounted));
      setSchedulerLastHeartbeatAt(String(detail.at || new Date().toISOString()));
    };
    window.addEventListener(MOUNT_EVENT, onMounted as EventListener);
    return () => {
      window.removeEventListener(MOUNT_EVENT, onMounted as EventListener);
    };
  }, []);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4">
        <h3 className="text-lg font-bold text-slate-900">Guardian Dashboard</h3>
        <p className="text-sm text-slate-500">Configure shift-end email risk visibility for operators and admins.</p>
      </div>
      {loading ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">Loading settings…</div>
      ) : (
        <div className="space-y-3">
          <div className={rowClass}>
            <p className={labelClass}>Enable Guardian Dashboard</p>
            <input type="checkbox" checked={settings.enabled} onChange={e => setSettings(prev => ({ ...prev, enabled: e.target.checked }))} />
          </div>
          <div className={rowClass}>
            <p className={labelClass}>Show to operators</p>
            <input type="checkbox" checked={settings.showToOperators} onChange={e => setSettings(prev => ({ ...prev, showToOperators: e.target.checked }))} />
          </div>
          <div className={rowClass}>
            <p className={labelClass}>Show to admins</p>
            <input type="checkbox" checked={settings.showToAdmins} onChange={e => setSettings(prev => ({ ...prev, showToAdmins: e.target.checked }))} />
          </div>
          <div className={rowClass}>
            <p className={labelClass}>Refresh interval seconds</p>
            <input className={inputClass} type="number" min={30} max={300} value={settings.refreshSeconds} onChange={e => setSettings(prev => ({ ...prev, refreshSeconds: Number(e.target.value || 60) }))} />
          </div>
          <div className={rowClass}>
            <p className={labelClass}>Look back for unresolved emails (hours)</p>
            <input className={inputClass} type="number" min={4} max={168} value={settings.guardianLookbackHours} onChange={e => setSettings(prev => ({ ...prev, guardianLookbackHours: Number(e.target.value || 24) }))} />
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Guardian Check Times</p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="text-xs text-slate-600">
                Timezone
                <input className={`${inputClass} mt-1`} value={settings.timezone} onChange={e => setSettings(prev => ({ ...prev, timezone: e.target.value || 'Africa/Dar_es_Salaam' }))} />
              </label>
              <label className="text-xs text-slate-600">
                Morning briefing time
                <input className={`${inputClass} mt-1`} type="time" value={settings.morningBriefTime} onChange={e => setSettings(prev => ({ ...prev, morningBriefTime: e.target.value || '08:00' }))} />
              </label>
              <label className="text-xs text-slate-600">
                Midday check time
                <input className={`${inputClass} mt-1`} type="time" value={settings.middayCheckTime} onChange={e => setSettings(prev => ({ ...prev, middayCheckTime: e.target.value || '13:00' }))} />
              </label>
              <label className="text-xs text-slate-600">
                End-of-day warning time
                <input className={`${inputClass} mt-1`} type="time" value={settings.endOfDayCheckTime} onChange={e => setSettings(prev => ({ ...prev, endOfDayCheckTime: e.target.value || '17:30' }))} />
              </label>
              <label className="text-xs text-slate-600">
                Shift finish time
                <input className={`${inputClass} mt-1`} type="time" value={settings.shiftEndTime} onChange={e => setSettings(prev => ({ ...prev, shiftEndTime: e.target.value || '18:00' }))} />
              </label>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-3">
              <label className="text-xs text-slate-600">
                <input className="mr-2" type="checkbox" checked={settings.morningBriefEnabled} onChange={e => setSettings(prev => ({ ...prev, morningBriefEnabled: e.target.checked }))} />
                Morning briefing enabled
              </label>
              <label className="text-xs text-slate-600">
                <input className="mr-2" type="checkbox" checked={settings.middayCheckEnabled} onChange={e => setSettings(prev => ({ ...prev, middayCheckEnabled: e.target.checked }))} />
                Midday check enabled
              </label>
              <label className="text-xs text-slate-600">
                <input className="mr-2" type="checkbox" checked={settings.endOfDayCheckEnabled} onChange={e => setSettings(prev => ({ ...prev, endOfDayCheckEnabled: e.target.checked }))} />
                End-of-day check enabled
              </label>
            </div>
          </div>
          <div className={rowClass}>
            <p className={labelClass}>Medium risk after minutes</p>
            <input className={inputClass} type="number" min={30} max={480} value={settings.mediumRiskAgeMinutes} onChange={e => setSettings(prev => ({ ...prev, mediumRiskAgeMinutes: Number(e.target.value || 120) }))} />
          </div>
          <div className={rowClass}>
            <p className={labelClass}>High risk after minutes</p>
            <input className={inputClass} type="number" min={60} max={720} value={settings.highRiskAgeMinutes} onChange={e => setSettings(prev => ({ ...prev, highRiskAgeMinutes: Number(e.target.value || 240) }))} />
          </div>
          <div className={rowClass}>
            <p className={labelClass}>Include unassigned emails</p>
            <input type="checkbox" checked={settings.includeUnassigned} onChange={e => setSettings(prev => ({ ...prev, includeUnassigned: e.target.checked }))} />
          </div>
          <div className={rowClass}>
            <p className={labelClass}>Operators can see emails assigned to others</p>
            <input type="checkbox" checked={settings.operatorsCanSeeAssignedToOthers} onChange={e => setSettings(prev => ({ ...prev, operatorsCanSeeAssignedToOthers: e.target.checked }))} />
          </div>
          <div className={rowClass}>
            <p className={labelClass}>Enable G.DASH actions</p>
            <input type="checkbox" checked={settings.gdashActionsEnabled} onChange={e => setSettings(prev => ({ ...prev, gdashActionsEnabled: e.target.checked }))} />
          </div>
          <div className={rowClass}>
            <p className={labelClass}>Allow admin override</p>
            <input type="checkbox" checked={settings.allowAdminOverride} onChange={e => setSettings(prev => ({ ...prev, allowAdminOverride: e.target.checked }))} />
          </div>
          <div className={rowClass}>
            <p className={labelClass}>Operators can assign to self</p>
            <input type="checkbox" checked={settings.operatorCanAssignToSelf} onChange={e => setSettings(prev => ({ ...prev, operatorCanAssignToSelf: e.target.checked }))} />
          </div>
          <div className={rowClass}>
            <p className={labelClass}>Operators can set follow-up</p>
            <input type="checkbox" checked={settings.operatorCanSetFollowup} onChange={e => setSettings(prev => ({ ...prev, operatorCanSetFollowup: e.target.checked }))} />
          </div>
          <div className={rowClass}>
            <p className={labelClass}>Operators can mark safe until tomorrow</p>
            <input type="checkbox" checked={settings.operatorCanMarkSafe} onChange={e => setSettings(prev => ({ ...prev, operatorCanMarkSafe: e.target.checked }))} />
          </div>
          <div className={rowClass}>
            <p className={labelClass}>Max items shown</p>
            <input className={inputClass} type="number" min={10} max={100} value={settings.maxItems} onChange={e => setSettings(prev => ({ ...prev, maxItems: Number(e.target.value || 50) }))} />
          </div>
          <div className={rowClass}>
            <p className={labelClass}>AI suggestions</p>
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">AI suggestions — coming in Phase 2</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Preview Guardian Popup</p>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => void openPreview('morning')} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700">Preview Morning Briefing</button>
              <button type="button" onClick={() => void openPreview('midday')} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700">Preview Midday Check</button>
              <button type="button" onClick={() => void openPreview('eod')} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700">Preview End-of-Day Warning</button>
              <button type="button" onClick={() => void openPreview('finish')} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700">Preview Shift Finish / Handover</button>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Run Scheduled Popup Now</p>
            <div className="flex flex-wrap gap-2">
              <button type="button" disabled={!schedulerMounted} onClick={() => triggerNow('morning')} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50">Trigger Morning Briefing Now</button>
              <button type="button" disabled={!schedulerMounted} onClick={() => triggerNow('midday')} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50">Trigger Midday Check Now</button>
              <button type="button" disabled={!schedulerMounted} onClick={() => triggerNow('eod')} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50">Trigger End-of-Day Warning Now</button>
              <button type="button" disabled={!schedulerMounted} onClick={() => triggerNow('finish')} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50">Trigger Shift Finish Now</button>
              <button type="button" onClick={clearTodayDismissals} className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800">Clear today dismissals</button>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Scheduler Diagnostics</p>
            <div className="grid grid-cols-1 gap-2 text-xs text-slate-700 md:grid-cols-2">
              <div>
                <span className="font-semibold">schedulerMounted:</span>{' '}
                {schedulerMounted ? 'true' : 'false'}
              </div>
              <div>
                <span className="font-semibold">schedulerLastHeartbeatAt:</span>{' '}
                {schedulerLastHeartbeatAt}
              </div>
              <div>
                <span className="font-semibold">Current {schedulerDiag?.timezone || settings.timezone} time:</span>{' '}
                {schedulerDiag?.currentTime || '--:--'}
              </div>
              <div>
                <span className="font-semibold">Next Guardian check:</span>{' '}
                {schedulerDiag?.nextCheckLabel || 'n/a'}
              </div>
              <div>
                <span className="font-semibold">Last scheduler check time:</span>{' '}
                {schedulerDiag?.lastCheckAt || 'n/a'}
              </div>
              <div>
                <span className="font-semibold">Last trigger decision:</span>{' '}
                {schedulerDiag?.lastDecision || 'n/a'}
              </div>
              <div className="md:col-span-2">
                <span className="font-semibold">Reason not triggered:</span>{' '}
                {schedulerDiag?.reason || 'n/a'}
              </div>
            </div>
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-2">
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">Last raw tick payload</p>
              <pre className="max-h-36 overflow-auto rounded bg-white p-2 text-[11px] leading-4 text-slate-700">
                {JSON.stringify(schedulerRawPayload || { note: 'No tick payload yet' }, null, 2)}
              </pre>
            </div>
          </div>
          <div className="pt-2">
            <button type="button" onClick={() => void save()} disabled={saving} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {saving ? 'Saving…' : 'Save G.DASH Settings'}
            </button>
            <button type="button" onClick={() => void testGuardian()} disabled={testing} className="ml-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-60">
              {testing ? 'Testing…' : 'Test Guardian'}
            </button>
            {status ? <span className="ml-3 text-sm text-slate-600">{status}</span> : null}
          </div>
          {testStatus ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              {testStatus}
            </div>
          ) : null}

          <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Guardian Daily Report</p>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <button type="button" className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700" onClick={() => setReportDate(new Date().toISOString().slice(0, 10))}>Today</button>
              <button type="button" className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700" onClick={() => setReportDate(new Date(Date.now() - 86400000).toISOString().slice(0, 10))}>Yesterday</button>
              <input type="date" className={inputClass} value={reportDate} onChange={e => setReportDate(e.target.value)} />
              <select className={inputClass} value={reportTeam} onChange={e => setReportTeam(e.target.value as any)}>
                <option value="all">All teams</option>
                <option value="reservations">Reservations</option>
                <option value="flight_planners">Flight planners</option>
              </select>
              <input className={inputClass} placeholder="Operator user ID (optional)" value={reportOperatorUserId} onChange={e => setReportOperatorUserId(e.target.value)} />
              <label className="text-xs text-slate-600"><input type="checkbox" className="mr-2" checked={reportIncludeDetails} onChange={e => setReportIncludeDetails(e.target.checked)} />Include details</label>
              <button type="button" onClick={() => void loadReport()} disabled={reportLoading} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700">{reportLoading ? 'Refreshing…' : 'Refresh'}</button>
              <button
                type="button"
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700"
                onClick={() => {
                  if (!reportData) return;
                  const header = ['Operator', 'User ID', 'Opened', 'Assigned', 'Follow-ups', 'Safe Tomorrow', 'Overrides', pendingLabel, 'Seen but untouched', 'Avg Response (min)', 'Last Active'];
                  const rows = reportData.operators.map((op) => [
                    op.name,
                    op.userId,
                    op.metrics.opened,
                    op.metrics.assignedToSelf,
                    op.metrics.followupsTomorrow,
                    op.metrics.safeTomorrow,
                    op.metrics.overrides,
                    op.metrics.unresolvedAfterInteraction ?? op.metrics.unresolvedAtShiftFinish,
                    op.metrics.seenButUntouched ?? op.metrics.noActionYetHighRisk ?? op.metrics.ignoredHighRisk,
                    op.metrics.averageResponseMinutes ?? '',
                    op.lastGdashActionAt || '',
                  ]);
                  const csv = [header, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
                  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `gdash-report-${reportData.date}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                Export CSV
              </button>
              <button type="button" className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700" onClick={() => window.print()}>
                Print-friendly view
              </button>
            </div>
            <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              This report tracks operator interaction with G.DASH items and whether those items were later resolved.
            </div>
            <div className="mb-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px] text-slate-600">
              <p className="mb-1 font-semibold text-slate-700">Metric legend</p>
              <p><span className="font-semibold">Opened:</span> operator clicked Open from G.DASH.</p>
              <p><span className="font-semibold">Assigned:</span> operator used Assign to me.</p>
              <p><span className="font-semibold">Follow-ups:</span> operator set follow-up for tomorrow.</p>
              <p><span className="font-semibold">Safe tomorrow:</span> operator marked safe/deferred to tomorrow.</p>
              <p><span className="font-semibold">Seen but untouched:</span> shown to operator, but no open/assign/follow-up/safe/override action taken.</p>
              <p><span className="font-semibold">Still unresolved after interaction:</span> operator touched item, but it remained operationally unresolved at snapshot time.</p>
            </div>
            {reportError ? <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{reportError}</div> : null}
            {reportData ? (
              <div className="space-y-3">
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Operator interaction metrics</p>
                  <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"><p className="text-[11px] text-slate-500">Total actions</p><p className="text-lg font-bold text-slate-900">{reportData.summary.totalActions}</p></div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"><p className="text-[11px] text-slate-500">Follow-ups created</p><p className="text-lg font-bold text-slate-900">{reportData.summary.totalFollowups}</p></div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"><p className="text-[11px] text-slate-500">Safe tomorrow</p><p className="text-lg font-bold text-slate-900">{reportData.summary.totalSafeTomorrow}</p></div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"><p className="text-[11px] text-slate-500">High-risk shown</p><p className="text-lg font-bold text-slate-900">{reportData.summary.highRiskShown ?? 0}</p></div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"><p className="text-[11px] text-slate-500">Overrides used</p><p className={`text-lg font-bold ${reportData.summary.totalOverrides > 0 ? 'text-amber-700' : 'text-slate-900'}`}>{reportData.summary.totalOverrides}</p></div>
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Outcome metrics</p>
                  <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"><p className="text-[11px] text-slate-500">Seen but untouched</p><p className={`text-lg font-bold ${metricToneClass(reportData.summary.seenButUntouched ?? reportData.summary.noActionYetHighRisk ?? 0)}`}>{reportData.summary.seenButUntouched ?? reportData.summary.noActionYetHighRisk ?? 0}</p></div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"><p className="text-[11px] text-slate-500">Operationally unresolved</p><p className={`text-lg font-bold ${(reportData.summary.unresolvedAfterInteraction ?? reportData.summary.unresolvedHighRisk) > 10 ? 'text-rose-700' : (reportData.summary.unresolvedAfterInteraction ?? reportData.summary.unresolvedHighRisk) > 0 ? 'text-amber-700' : 'text-slate-900'}`}>{reportData.summary.unresolvedAfterInteraction ?? reportData.summary.unresolvedHighRisk}</p></div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"><p className="text-[11px] text-slate-500">High-risk still unresolved</p><p className={`text-lg font-bold ${reportData.summary.unresolvedHighRisk > 10 ? 'text-rose-700' : reportData.summary.unresolvedHighRisk > 0 ? 'text-amber-700' : 'text-slate-900'}`}>{reportData.summary.unresolvedHighRisk}</p></div>
                  </div>
                </div>
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="min-w-full text-left text-xs">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-3 py-2">Operator</th><th className="px-3 py-2">Opened</th><th className="px-3 py-2">Assigned</th><th className="px-3 py-2">Follow-ups</th><th className="px-3 py-2">Safe Tomorrow</th><th className="px-3 py-2">Overrides</th><th className="px-3 py-2" title="Items operator interacted with that remained operationally unresolved later.">{pendingLabel}</th><th className="px-3 py-2" title="Items shown to operator where no action was taken.">Seen but untouched</th><th className="px-3 py-2">Avg Response</th><th className="px-3 py-2">Last Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.operators.map((op) => (
                        <React.Fragment key={op.userId}>
                          <tr className="border-t border-slate-100 cursor-pointer hover:bg-slate-50" onClick={() => setExpandedOperators(prev => ({ ...prev, [op.userId]: !prev[op.userId] }))}>
                            <td className="px-3 py-2">
                              <button type="button" className="font-semibold text-slate-800 hover:underline" onClick={(e) => { e.stopPropagation(); setExpandedOperators(prev => ({ ...prev, [op.userId]: !prev[op.userId] })); }}>
                                {op.name}
                              </button>
                              {op.unknownUserId ? <p className="text-[10px] text-slate-500">No active account found · {op.userIdShort || op.userId.slice(0, 8)}</p> : null}
                            </td>
                            <td className="px-3 py-2">{op.metrics.opened}</td>
                            <td className="px-3 py-2">{op.metrics.assignedToSelf}</td>
                            <td className="px-3 py-2">{op.metrics.followupsTomorrow}</td>
                            <td className="px-3 py-2">{op.metrics.safeTomorrow}</td>
                            <td className={`px-3 py-2 ${op.metrics.overrides > 0 ? 'text-amber-700 font-semibold' : ''}`}>{op.metrics.overrides}</td>
                            <td className={`px-3 py-2 ${metricToneClass(op.metrics.unresolvedAfterInteraction ?? op.metrics.unresolvedAtShiftFinish)}`}>{op.metrics.unresolvedAfterInteraction ?? op.metrics.unresolvedAtShiftFinish}</td>
                            <td className={`px-3 py-2 ${metricToneClass(op.metrics.seenButUntouched ?? op.metrics.noActionYetHighRisk ?? op.metrics.ignoredHighRisk)}`}>{op.metrics.seenButUntouched ?? op.metrics.noActionYetHighRisk ?? op.metrics.ignoredHighRisk}</td>
                            <td className="px-3 py-2">{op.metrics.averageResponseMinutes === null ? '—' : `${op.metrics.averageResponseMinutes}m`}</td>
                            <td className="px-3 py-2">{op.lastGdashActionAt ? new Date(op.lastGdashActionAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                          </tr>
                          {expandedOperators[op.userId] && reportIncludeDetails ? (
                            <tr className="border-t border-slate-100 bg-slate-50">
                              <td colSpan={10} className="px-3 py-2">
                                {op.activeThreads.length === 0 ? (
                                  <p className="text-[11px] text-slate-500">No active threads.</p>
                                ) : (
                                  <div className="space-y-1">
                                    {op.activeThreads.map((t) => (
                                      <div key={`${op.userId}-${t.threadId}`} className="rounded border border-slate-200 bg-white px-2 py-1">
                                        <p className="text-[12px] font-semibold text-slate-800">{t.subject}</p>
                                        <p className="text-[11px] text-slate-500">Risk: {t.riskLevel || 'UNKNOWN'} · Current state: {t.latestCaseState || t.caseState || t.status} · Direction: {t.latestDirection || 'UNKNOWN'} · Assigned: {t.assignedToName || 'Unassigned'}</p>
                                        <p className="text-[11px] text-slate-500">Last action: {t.lastAction} {t.lastActionAt ? `at ${new Date(t.lastActionAt).toLocaleString()}` : ''} {t.followUpDueAt ? `· Follow-up due ${new Date(t.followUpDueAt).toLocaleString()}` : ''}</p>
                                        <p className="text-[11px] text-slate-500">Touched: {t.operatorTouched ? 'Yes' : 'No'} · Still operationally unresolved: {t.stillOperationallyUnresolved ? 'Yes' : 'No'}</p>
                                        <div className="mt-1">
                                          <button
                                            type="button"
                                            className="rounded border border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-700"
                                            onClick={() => {
                                              const params = new URLSearchParams();
                                              if (t.messageId) params.set('openMessageId', t.messageId);
                                              params.set('threadId', t.threadId);
                                              window.location.hash = `#/inbox?${params.toString()}`;
                                            }}
                                          >
                                            Open
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
      <GDashPopup
        open={previewOpen}
        preview
        checkType={previewType}
        summary={previewSummary}
        onRefreshSummary={setPreviewSummary}
        onClose={() => setPreviewOpen(false)}
        onOpenInbox={undefined}
        onRemindLater={undefined}
      />
    </div>
  );
};
